import { models, helpers } from '@kurtosys/ksys-app-template';
import { ConfigStore } from '../../stores';

const searchParams = new URLSearchParams(location.search);
const isDebug = searchParams.has('ksysAppDebug') === true;
if (isDebug) {
    console.log(
        '%cDebugging Enabled',
        'color: hotpink; font-size: 1.5em; font-weight: bold;'
    );
}

export enum Codes {
    LINK_ACTION_REQUIRED_FIELDS,
}

const MESSAGES = {
    [Codes.LINK_ACTION_REQUIRED_FIELDS]:
        'Link action requires either a linkId or a linkFilter object',
};

export interface ErrorReference {
    code?: Codes;
    debug?: {
        message: string;
        conditional?: models.common.IConditional;
    };
    data?: any;
    configStore?: ConfigStore;
    relatedConfigPath?: string;
}

export interface LogOptions {
    messageStyling?: Array<string>;
}

type LogType = 'error' | 'warn' | 'info' | 'debug' | 'trace';

export class ErrorTracking {
    static uniqueMessages: Set<string> = new Set();
    static log(
        type: LogType,
        errorRef: ErrorReference,
        logOptions?: LogOptions
    ) {
        let log = false;
        switch (type) {
            case 'error':
            case 'warn':
                log = true;
                break;
            case 'debug':
            case 'trace':
                if (isDebug) {
                    log = true;
                }
                break;
            case 'info':
            default:
                log = true;
        }
        if (log) {
            ErrorTracking.logMessage(type, errorRef, logOptions);
        }
    }
    private static logMessage(
        type: LogType,
        errorRef: ErrorReference,
        logOptions?: LogOptions
    ) {
        const messageKey = ErrorTracking.messageKey(type, errorRef, logOptions);
        const skipUniqueCheck = type === 'debug';
        if (skipUniqueCheck || !ErrorTracking.uniqueMessages.has(messageKey)) {
            ErrorTracking.uniqueMessages.add(messageKey);
            let message;
            if (
                ['debug', 'trace'].indexOf(type) >= 0 &&
                errorRef.debug?.message
            ) {
                let applyDebugMessage = true;
                if (errorRef.debug?.conditional) {
                    const conditional = new helpers.ConditionalHelper(
                        errorRef.debug.conditional
                    );
                    const isValid = conditional.matchesWithOptions({
                        instance: errorRef.data,
                    });
                    if (!isValid) {
                        applyDebugMessage = false;
                    }
                }
                if (applyDebugMessage) {
                    message = errorRef.debug?.message;
                }
            } else if (errorRef.code) {
                message = MESSAGES[errorRef.code];
            }
            if (message) {
                const messages = [`%c${type}:%c ${message}`];
                if (
                    logOptions &&
                    logOptions.messageStyling &&
                    logOptions.messageStyling.length > 0
                ) {
                    messages.push(...logOptions.messageStyling);
                } else {
                    const commonStyles =
                        'font-weight: bold; font-size: 1.2em; text-transform: uppercase;';
                    switch (type) {
                        case 'error':
                            messages.push(`color: red; ${commonStyles}`);
                            break;
                        case 'warn':
                            messages.push(`color: orange; ${commonStyles}`);
                            break;
                        case 'info':
                            messages.push(`color: lightblue; ${commonStyles}`);
                            break;
                        case 'debug':
                            messages.push(`color: hotpink; ${commonStyles}`);
                            break;
                        case 'trace':
                            messages.push(`color: magenta; ${commonStyles}`);
                            break;
                        default:
                            messages.push(`color: white; ${commonStyles}`);
                            break;
                    }
                    messages.push('color: lightgrey; font-weight: normal;');
                }
                console.group('Fund Center Log');
                if (type === 'trace') {
                    console.trace(...messages);
                } else {
                    console.log(...messages);
                }
                ErrorTracking.logData(errorRef, logOptions);
                console.groupEnd();
            }
        }
    }
    private static logData(errorRef: ErrorReference, logOptions?: LogOptions) {
        const data = errorRef.data;
        if (data !== undefined) {
            if (
                Array.isArray(data) &&
                data.length > 0 &&
                typeof data[0] === 'object'
            ) {
                console.table(data);
            } else {
                console.log(data);
            }
        }
    }
    private static messageKey(
        type: LogType,
        errorRef: ErrorReference,
        logOptions?: LogOptions
    ) {
        const { code, configStore } = errorRef;
        const baseKey = `${type}-${code}`;
        if (configStore) {
            ErrorTracking.appendToMessageKey(
                baseKey,
                'type',
                configStore.selectedFundTypeId
            );
            ErrorTracking.appendToMessageKey(
                baseKey,
                'module',
                configStore.currentModuleId
            );
        }
        ErrorTracking.appendToMessageKey(
            baseKey,
            'config',
            errorRef.relatedConfigPath
        );
        return baseKey;
    }
    private static appendToMessageKey(
        baseKey: string,
        label: string,
        value: string
    ) {
        if (value) {
            baseKey = `${baseKey}-${label}{${value}}`;
        }
    }
}



// WEBPACK FOOTER //
// ./src/utils/errorHandling/ErrorTracking.ts